import { NO_PROXY } from '@hit-game/shared/constants';
import type { IProxy } from '@hit-game/shared/types';
import { UserPlus, Users, Wallet, Zap } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccountAddForm } from '../../components/account/AccountAddForm';
import { AccountBulkRegisterPanel } from '../../components/account/AccountBulkRegisterPanel';
import { AccountRegisterForm } from '../../components/account/AccountRegisterForm';
import { AccountRow } from '../../components/account/AccountRow';
import { PageHeader } from '../../components/account/PageHeader';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { DataToolbar } from '../../components/common/DataToolbar';
import { Pagination } from '../../components/common/Pagination';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { useMutation } from '../../hooks/useMutation';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { fetchAllPages } from '../../lib/http';
import { accountService, type AccountWithSecret } from '../../services/account.service';
import { proxyService } from '../../services/proxy.service';

const COLUMNS = ['Username', 'Tên hiển thị', 'Mật khẩu', 'Proxy', 'Ngày tạo', 'Hành động'];

type Tab = 'manual' | 'bulk';

const TAB_CLASS = (active: boolean): string =>
  `rounded-md px-4 py-2 text-[13px] font-semibold transition-colors ${
    active ? 'bg-accent text-white' : 'text-ink-dim hover:text-ink'
  }`;

export function AccountsPage() {
  const toast = useToast();
  const confirm = useConfirm();

  // `withPassword` — trang này là Kho tài khoản nên được xem mật khẩu, phải xin tường minh.
  const fetcher = useCallback(
    (params: { page: number; limit: number; search?: string }) =>
      accountService.list({ ...params, withPassword: true }),
    [],
  );
  const list = usePaginatedList<AccountWithSecret>(fetcher);

  const [tab, setTab] = useState<Tab>('manual');

  // Danh sách proxy để gán — tải riêng, không phân trang theo bảng tài khoản.
  const [proxies, setProxies] = useState<IProxy[]>([]);

  const loadProxies = useCallback(() => {
    /** Cả kho, không phải một trang: thiếu proxy nào là dropdown không mời chọn được nó. */
    const load = async () => {
      try {
        setProxies(await fetchAllPages((params) => proxyService.list(params)));
      } catch {
        setProxies([]);
      }
    };
    void load();
  }, []);

  useEffect(loadProxies, [loadProxies]);

  /**
   * Tải lại cả bảng tài khoản lẫn kho proxy.
   *
   * Gọi sau MỌI thao tác đổi quan hệ tài khoản ↔ proxy (thêm, xoá, gán lại):
   * proxy vừa gán không còn rảnh, proxy của tài khoản vừa xoá thì rảnh trở lại —
   * chỉ tải lại một bên là dropdown mời chọn proxy đã có chủ.
   */
  const reloadAccountsAndProxies = () => {
    list.reload();
    loadProxies();
  };

  /** Thao tác đổi quan hệ tài khoản ↔ proxy → phải tải lại CẢ HAI. */
  const mutate = useMutation(reloadAccountsAndProxies);
  /** Thao tác chỉ đụng bản ghi tài khoản → kho proxy không đổi, khỏi tải lại. */
  const mutateAccountOnly = useMutation(list.reload);

  const assignProxy = (accountId: string, proxy: string) =>
    mutate(() => accountService.assignProxy(accountId, proxy === NO_PROXY ? null : proxy));

  const refreshSession = async (accountId: string): Promise<void> => {
    // Đăng nhập lại chỉ làm mới token của chính tài khoản đó — không đụng tới
    // proxy nào, nên không kéo lại cả kho proxy. Lỗi thì interceptor đã toast.
    await mutateAccountOnly(() => accountService.refreshSession(accountId));
  };

  const remove = async (account: AccountWithSecret) => {
    const accepted = await confirm({
      title: 'Xoá tài khoản',
      message: `Xoá "${account.username}" khỏi hệ thống? Proxy đang gán sẽ được trả về kho.`,
      confirmText: 'Xoá',
      type: 'danger',
    });
    if (!accepted) return;

    if (!(await mutate(() => accountService.remove(String(account._id))))) {
      toast.error('Không xoá được tài khoản');
    }
  };

  const isEmpty = !list.loading && list.items.length === 0;

  return (
    <div className="text-ink space-y-6">
      <PageHeader icon={Wallet} title="Kho tài khoản" subtitle="Thêm và quản lý tài khoản HIT" />

      <div className="border-line bg-canvas inline-flex rounded-lg border p-0.5">
        <button
          type="button"
          onClick={() => {
            setTab('manual');
          }}
          className={TAB_CLASS(tab === 'manual')}
        >
          <UserPlus size={14} className="mr-1.5 inline-block align-text-top" />
          Nhập thủ công
        </button>
        <button
          type="button"
          onClick={() => {
            setTab('bulk');
          }}
          className={TAB_CLASS(tab === 'bulk')}
        >
          <Zap size={14} className="mr-1.5 inline-block align-text-top" />
          Tạo hàng loạt
        </button>
      </div>

      {tab === 'manual' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <AccountRegisterForm proxies={proxies} onRegistered={reloadAccountsAndProxies} />
          <AccountAddForm proxies={proxies} onAdded={reloadAccountsAndProxies} />
        </div>
      ) : (
        <AccountBulkRegisterPanel proxies={proxies} onCreated={reloadAccountsAndProxies} />
      )}

      <div className="border-line bg-panel overflow-hidden rounded-xl border">
        <DataToolbar
          icon={<Users size={16} />}
          title="Danh sách tài khoản"
          count={list.total}
          searchValue={list.search}
          onSearchChange={list.setSearch}
          searchPlaceholder="Tìm theo username hoặc tên hiển thị..."
        />

        <DataStateMessage
          loading={list.loading}
          empty={isEmpty ? 'Chưa có tài khoản' : null}
          emptyHint={isEmpty ? 'Dùng form "Đăng nhập tài khoản" ở trên để thêm' : undefined}
          emptyIcon={<Users size={32} className="text-line" />}
        />

        {!list.loading && list.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-canvas-raised">
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      className={`text-ink-faint px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                        column === 'Hành động' ? 'text-center' : ''
                      }`}
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.items.map((account) => (
                  <AccountRow
                    key={String(account._id)}
                    account={account}
                    proxies={proxies}
                    onAssignProxy={assignProxy}
                    onRefreshSession={refreshSession}
                    onDelete={(item) => {
                      void remove(item);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={list.page}
          totalPages={list.totalPages}
          onPageChange={list.setPage}
          loading={list.loading}
        />
      </div>
    </div>
  );
}
